import { useState, useEffect } from 'react';
import { Loader2, ChevronDown, ChevronUp, GitMerge, Copy, AlertTriangle, Hash } from 'lucide-react';
import api from '@/api';

export function DedupTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { loadLogs(); }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetDedupLogs(30);
      setLogs(data.logs || []);
    } catch { }
    setLoading(false);
  };

  const loadDetail = async (logId) => {
    if (expandedLog === logId) { setExpandedLog(null); return; }
    setExpandedLog(logId);
    setDetailLoading(true);
    try {
      const data = await api.adminGetDedupLogDetail(logId);
      setDetail(data);
    } catch { }
    setDetailLoading(false);
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="text-center py-10"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>;

  if (logs.length === 0) {
    return (
      <div className="mt-2">
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-100">
          <GitMerge size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-semibold text-gray-500">No matching logs yet</p>
          <p className="text-xs text-gray-400 mt-1">Logs will appear after the next 5 AM ingestion run</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-3" data-testid="dedup-tab">
      <p className="text-xs text-gray-400">3-Layer matching pipeline results. Refreshed daily after 5 AM GMT ingestion.</p>

      {logs.map(log => {
        const isExpanded = expandedLog === log.id;
        const total = log.total_incoming || 0;
        const l1 = log.layer1_exact_matches || 0;
        const l2 = log.layer2_merges || 0;
        const l3 = log.layer3_grey_zone || 0;
        const added = log.new_topics_added || 0;
        // Silent-drop accounting (added April 2026).
        // Older log rows may not have these fields → fall back to a computed residual.
        const skippedLower = log.skipped_lower_score || 0;
        const droppedNoRegion = log.dropped_no_region || 0;
        const droppedOther = log.dropped_other ?? Math.max(0, total - (added + l1 + l2 + l3 + skippedLower + droppedNoRegion));
        const classified = added + l1 + l2 + l3 + skippedLower + droppedNoRegion + droppedOther;
        const isBalanced = classified === total;
        const hasDropMetrics = log.skipped_lower_score !== undefined || log.dropped_no_region !== undefined;
        const dedupPct = total > 0 ? Math.round(((l1 + l2) / total) * 100) : 0;

        return (
          <div key={log.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <button
              data-testid={`dedup-log-${log.id}`}
              onClick={() => loadDetail(log.id)}
              className="w-full px-4 py-3.5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold text-gray-900">{formatDate(log.timestamp)}</span>
                  {log.embeddings_used && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 uppercase">k-NN</span>
                  )}
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{dedupPct}% deduped</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] items-center">
                  <span className="text-gray-500">{total} incoming</span>
                  <span className="text-green-600 font-semibold">+{added} new</span>
                  <span className="text-blue-600 font-semibold">{l1} exact</span>
                  <span className="text-violet-600 font-semibold">{l2} merged</span>
                  {l3 > 0 && <span className="text-amber-600 font-semibold">{l3} grey</span>}
                  {skippedLower > 0 && (
                    <span className="text-slate-500 font-semibold" title="Exact-title match but existing trend score was already higher — not re-counted as new or refresh.">
                      {skippedLower} skipped
                    </span>
                  )}
                  {droppedNoRegion > 0 && (
                    <span className="text-rose-500 font-semibold" title="Dropped because source region is not in US/GB/IN.">
                      {droppedNoRegion} no-region
                    </span>
                  )}
                  {droppedOther > 0 && (
                    <span className="text-gray-400 font-semibold" title="Residual — any other silent drop (currently rare).">
                      {droppedOther} other
                    </span>
                  )}
                  {hasDropMetrics && (
                    <span
                      className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isBalanced ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                      title={isBalanced ? 'Math balances: all incoming records accounted for.' : 'Residual unaccounted — see "other".'}
                    >
                      {isBalanced ? 'balanced' : 'residual'}
                    </span>
                  )}
                </div>
              </div>
              {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100 px-4 py-3">
                {detailLoading ? (
                  <div className="text-center py-6"><Loader2 size={18} className="animate-spin mx-auto text-gray-400" /></div>
                ) : detail ? (
                  <div className="space-y-4">
                    {/* Summary Cards — full balance breakdown */}
                    <div className="grid grid-cols-4 gap-2">
                      <SummaryCard icon={Copy} label="New" sublabel="Added" value={detail.new_topics_added || 0} color="green" />
                      <SummaryCard icon={Hash} label="Layer 1" sublabel="Exact refresh" value={detail.layer1_exact_matches || 0} color="blue" />
                      <SummaryCard icon={GitMerge} label="Layer 2" sublabel="Merged" value={detail.layer2_merges || 0} color="violet" />
                      <SummaryCard icon={AlertTriangle} label="Layer 3" sublabel="Grey zone" value={detail.layer3_grey_zone || 0} color="amber" />
                    </div>
                    {(detail.skipped_lower_score !== undefined || detail.dropped_no_region !== undefined) && (
                      <div className="grid grid-cols-3 gap-2">
                        <SummaryCard icon={Copy} label="Skipped" sublabel="No upgrade" value={detail.skipped_lower_score || 0} color="slate" />
                        <SummaryCard icon={AlertTriangle} label="Dropped" sublabel="No region" value={detail.dropped_no_region || 0} color="rose" />
                        <SummaryCard icon={Hash} label="Residual" sublabel="Other" value={detail.dropped_other || 0} color="gray" />
                      </div>
                    )}

                    {/* Layer 2: Merge Entries */}
                    {detail.merge_entries && detail.merge_entries.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-violet-600 tracking-widest uppercase mb-2">
                          Layer 2 — Merged ({detail.merge_entries.length})
                        </p>
                        <div className="space-y-1.5 max-h-60 overflow-y-auto">
                          {detail.merge_entries.map((e, i) => (
                            <div key={i} className="bg-violet-50/50 rounded-lg px-3 py-2 text-xs">
                              <div className="flex items-start gap-2">
                                <GitMerge size={12} className="text-violet-500 mt-0.5 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-gray-700 font-medium truncate">{e.incoming_title}</p>
                                  <p className="text-gray-400 truncate mt-0.5">merged into: {e.merged_into}</p>
                                  <div className="flex gap-2 mt-1">
                                    <span className="text-[9px] font-bold text-violet-600">cos: {e.cosine_score}</span>
                                    <span className="text-[9px] text-gray-400">{e.incoming_source} → {e.merged_source}</span>
                                    <span className="text-[9px] text-gray-400">{e.category}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Layer 3: Grey Zone Entries */}
                    {detail.grey_zone_entries && detail.grey_zone_entries.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-amber-600 tracking-widest uppercase mb-2">
                          Layer 3 — Grey Zone ({detail.grey_zone_entries.length})
                        </p>
                        <div className="space-y-1.5 max-h-60 overflow-y-auto">
                          {detail.grey_zone_entries.map((e, i) => (
                            <div key={i} className="bg-amber-50/50 rounded-lg px-3 py-2 text-xs">
                              <div className="flex items-start gap-2">
                                <AlertTriangle size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-gray-700 font-medium truncate">{e.incoming_title}</p>
                                  <p className="text-gray-400 truncate mt-0.5">similar to: {e.matched_title}</p>
                                  <div className="flex gap-2 mt-1">
                                    <span className="text-[9px] font-bold text-amber-600">cos: {e.cosine_score}</span>
                                    <span className="text-[9px] text-gray-400">{e.incoming_source} ~ {e.matched_source}</span>
                                    <span className="text-[9px] text-gray-400">{e.category}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No entries */}
                    {(!detail.merge_entries || detail.merge_entries.length === 0) &&
                     (!detail.grey_zone_entries || detail.grey_zone_entries.length === 0) && (
                      <p className="text-xs text-gray-400 text-center py-3">No merge or grey zone entries in this run</p>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, sublabel, value, color }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700',
    violet: 'bg-violet-50 text-violet-700',
    amber: 'bg-amber-50 text-amber-700',
    green: 'bg-green-50 text-green-700',
    slate: 'bg-slate-50 text-slate-700',
    rose: 'bg-rose-50 text-rose-700',
    gray: 'bg-gray-50 text-gray-700',
  };
  return (
    <div className={`${colorMap[color]} rounded-xl p-2.5 text-center`}>
      <Icon size={14} className="mx-auto mb-1 opacity-60" />
      <p className="text-lg font-extrabold">{value}</p>
      <p className="text-[8px] font-bold tracking-wider uppercase opacity-70">{label}</p>
      <p className="text-[8px] opacity-50">{sublabel}</p>
    </div>
  );
}
